import React from 'react'
import { useTheme, IconButton, Box } from '@mui/material'
import { J1LeftarrowA, J1LeftarrowD, J1RightarrowA, J1RightarrowD } from '@e3dc-react/icons'
import { makeStyles } from 'tss-react/mui'

interface StyleProps {
  isMobilePaginator?: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  arrowWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.secondary.main,
    '& svg': {
      fontSize: 30,
    },
  },
  arrows: {
    padding: props.isMobilePaginator ? 0 : '',
  },
}))

interface Props {
  count: number
  page: number
  rowsPerPage: number
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void
  setCurrPage?: (value: number) => void
  isMobilePaginator?: boolean
}

/**
 * Component provides forward and backward page actions
 */
const TablePaginationActions: React.VFC<Props> = ({ count, page, rowsPerPage, onPageChange, setCurrPage, isMobilePaginator = false }) => {
  const { classes } = useStyles({ isMobilePaginator })
  const theme = useTheme()
  const lastPage = Math.ceil(count / rowsPerPage) - 1

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement> | null): void => {
    onPageChange(event, page - 1)
    setCurrPage?.(page - 1)
  }

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement> | null): void => {
    onPageChange(event, page + 1)
    setCurrPage?.(page + 1)
  }

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement> | null): void => {
    onPageChange(event, 0)
    setCurrPage?.(0)
  }

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement> | null): void => {
    onPageChange(event, lastPage)
    setCurrPage?.(lastPage)
  }

  return (
    <Box className={classes.arrowWrapper}>
      <IconButton
        className={classes.arrows}
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
        data-qa={'pagination-firstPage'}
        size="large"
      >
        {theme.direction === 'rtl' ? <J1RightarrowA /> : <J1LeftarrowA />}
      </IconButton>
      <IconButton
        className={classes.arrows}
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
        data-qa={'pagination-prev'}
        size="large"
      >
        {theme.direction === 'rtl' ? <J1RightarrowD /> : <J1LeftarrowD />}
      </IconButton>
      <IconButton
        className={classes.arrows}
        onClick={handleNextButtonClick}
        disabled={page >= lastPage}
        aria-label="next page"
        data-qa={'pagination-next'}
        size="large"
      >
        {theme.direction === 'rtl' ? <J1LeftarrowD /> : <J1RightarrowD />}
      </IconButton>
      <IconButton
        className={classes.arrows}
        onClick={handleLastPageButtonClick}
        disabled={page === lastPage || count === 0}
        aria-label="last page"
        data-qa={'pagination-lastPage'}
        size="large"
      >
        {theme.direction === 'rtl' ? <J1LeftarrowA /> : <J1RightarrowA />}
      </IconButton>
    </Box>
  )
}

export default TablePaginationActions
